import type {
  IntegrationToolCatalogEntry,
  RemoteRiskClass,
  RemoteToolDefect,
  RemoteToolLifecycle,
} from "@playbookai/api-clients";
import type { StatusTone } from "@playbookai/ui";

/**
 * ADR-0071 — the governed remote-tool catalogue, as an administrator governs it.
 *
 * The workbench's rules, extracted from the component so each can be asserted
 * directly instead of by driving a table: what a lifecycle is CALLED, what the
 * server's untrusted annotation says, what a classification control shows before
 * anybody confirms it, and which of a selection a decision may actually be
 * written to.
 *
 * PROVIDER-NEUTRAL, like every other file in the integration core. `toolName` is
 * an opaque string a remote server chose; nothing below reads it, parses it, or
 * branches on a provider key (ADR-0042).
 */

/** The lifecycles a review may WRITE. `withdrawn` is the server's fact, not ours. */
export type ReviewableLifecycle = Exclude<RemoteToolLifecycle, "withdrawn">;

/** A classification control's value. `""` is the not-yet-confirmed sentinel. */
export type RiskDraft = RemoteRiskClass | "";

/** The catalogue's own "confirmed class" filter. */
export type RiskFilter = "all" | RemoteRiskClass | "unclassified";

export const LIFECYCLE_LABEL: Readonly<Record<RemoteToolLifecycle, string>> = {
  quarantined: "Needs review",
  enabled: "Enabled",
  disabled: "Disabled",
  withdrawn: "Withdrawn",
};

/**
 * A tool the server had stopped offering and now offers again.
 *
 * It comes back QUARANTINED with its risk class, reviewer and review time
 * cleared — which is identical to a tool nobody has ever reviewed, and the two
 * are different questions for someone draining a review queue. The distinction
 * is a durable fact (`withdrawnAt` survives the return and is cleared by the
 * next review), never inferred from timestamps drifting apart.
 */
export function hasReturned(entry: IntegrationToolCatalogEntry): boolean {
  return entry.lifecycle === "quarantined" && entry.withdrawnAt !== null;
}

/**
 * The status a reviewer reads, which is the lifecycle plus that one distinction.
 *
 * Deliberately NOT a fifth lifecycle: `withdrawn` remains the server's fact and
 * `quarantined` remains the governance state, so nothing downstream has a new
 * value to handle. Only the words change.
 */
export function lifecycleLabel(entry: IntegrationToolCatalogEntry): string {
  return hasReturned(entry)
    ? "Returned — review required"
    : LIFECYCLE_LABEL[entry.lifecycle];
}

/**
 * `quarantined` is `watch` and not `neutral`: a decision nobody has made yet is
 * the queue this page exists to drain, so it must not read as a resting state.
 * `withdrawn` is `critical` because it is the one lifecycle no review can move.
 */
export const LIFECYCLE_TONE: Readonly<Record<RemoteToolLifecycle, StatusTone>> =
  {
    quarantined: "watch",
    enabled: "healthy",
    disabled: "neutral",
    withdrawn: "critical",
  };

export const RISK_LABEL: Readonly<Record<RemoteRiskClass, string>> = {
  read: "Read",
  write: "Write",
};

export const DEFECT_LABEL: Readonly<Record<RemoteToolDefect, string>> = {
  schema_missing: "the server sent no schema",
  schema_too_large: "the schema is too large to store",
  schema_unsupported:
    "the schema uses constructs this platform cannot build a validator from",
  presentation_policy_mismatch:
    "this tool's form policy no longer matches the schema the server offers, so a fixed destination would be editable",
};

/** The entry's own name for a human. Never the id, never a parsed tool name. */
export function toolLabel(entry: IntegrationToolCatalogEntry): string {
  return entry.title ?? entry.toolName;
}

/**
 * What the SERVER claims about a tool, in words — `null` when it claimed
 * nothing.
 *
 * `readOnlyHint` is mutable remote metadata: a suggestion a reviewer compares
 * against the class they are about to confirm, never a permission. An ABSENT
 * annotation is reported as an absence rather than guessed in either direction:
 * "the server did not say" and "the server said this writes" are different
 * facts, and collapsing them is how an unverified claim becomes policy.
 */
export function serverHint(
  annotations: Record<string, unknown>,
): string | null {
  const claim = annotations["readOnlyHint"];
  if (claim === true) return "Read-only";
  if (claim === false) return "May modify data";
  return null;
}

/**
 * What the classification control shows before an administrator touches it.
 *
 * A CONFIRMED class wins. Failing that, an entry still awaiting review is
 * prefilled from the connector's `suggestedRiskClass`, so the common case is one
 * click — and that prefill governs nothing. It is not sent anywhere, it does not
 * enable the tool, and the entry stays `Needs review` until the review dialog is
 * explicitly confirmed.
 */
export function initialRiskDraft(
  entry: IntegrationToolCatalogEntry,
): RiskDraft {
  if (entry.riskClass !== null) return entry.riskClass;
  if (entry.lifecycle === "quarantined") return entry.suggestedRiskClass ?? "";
  return "";
}

/**
 * The line under a classification that is NOT the one on record, or null when
 * the control shows the stored value.
 *
 * Two drafts, said differently on purpose: a prefill the connector suggested is
 * advice, and a value the administrator picked is their own unsaved intent.
 * Neither is policy, and both need "confirm to apply" said out loud, because the
 * control looks identical to one that has already been saved.
 */
export function riskDraftNote(
  entry: IntegrationToolCatalogEntry,
  draft: RiskDraft,
): string | null {
  if (draft === "" || entry.riskClass === draft) return null;
  return entry.riskClass === null && draft === entry.suggestedRiskClass
    ? "Suggested — confirm to apply"
    : "Not confirmed — confirm to apply";
}

export interface ReviewCandidate {
  readonly entry: IntegrationToolCatalogEntry;
  /** The classification the administrator is about to confirm. */
  readonly riskClass: RemoteRiskClass | null;
}

export interface SkippedCandidate {
  readonly entry: IntegrationToolCatalogEntry;
  readonly reason: string;
}

export interface ReviewPlan {
  readonly apply: readonly ReviewCandidate[];
  readonly skipped: readonly SkippedCandidate[];
}

/**
 * Split a selection into what this decision can be written to and what it
 * cannot, with the reason in words.
 *
 * Three refusals, all of them the server's own rules restated so an
 * administrator reads them BEFORE the request rather than as a failed write:
 *
 *  - a `withdrawn` entry is not reviewable at all — the review route's request
 *    type excludes the value, and the tool is gone from the server;
 *  - an enable needs a CONFIRMED class, because `null` is never callable and the
 *    server's own hint is not a substitute for one;
 *  - an enable needs a defect-free schema, because arguments that cannot be
 *    validated against what was approved cannot be approved.
 *
 * A `disabled` or `quarantined` decision carries neither of the last two: taking
 * access away from an unclassified or defective tool is always allowed.
 */
export function planReview(
  candidates: readonly ReviewCandidate[],
  lifecycle: ReviewableLifecycle,
): ReviewPlan {
  const apply: ReviewCandidate[] = [];
  const skipped: SkippedCandidate[] = [];
  for (const candidate of candidates) {
    const { entry } = candidate;
    if (entry.lifecycle === "withdrawn") {
      skipped.push({
        entry,
        reason: "The server no longer offers this tool.",
      });
    } else if (lifecycle === "enabled" && entry.defect !== null) {
      skipped.push({
        entry,
        reason: `Cannot be enabled — ${DEFECT_LABEL[entry.defect]}.`,
      });
    } else if (lifecycle === "enabled" && candidate.riskClass === null) {
      skipped.push({
        entry,
        reason: "Confirm a classification before enabling.",
      });
    } else {
      apply.push(candidate);
    }
  }
  return { apply, skipped };
}

export interface ReviewOutcomeNotice {
  readonly title: string;
  readonly body: string;
}

function toolCount(count: number): string {
  return count === 1 ? "1 tool" : `${count} tools`;
}

/**
 * What a bulk review that did not fully succeed ACTUALLY did.
 *
 * There is no bulk review route by design: the confirmation issues one write per
 * entry, each with its own tenant scoping and its own audit row, settled
 * independently. So a refusal does not roll the siblings back — the entries that
 * succeeded are already live policy. Reporting "nothing was partially applied"
 * would be a false statement about what the workspace can currently reach, and
 * it would send an administrator to re-confirm the whole selection as though it
 * were a repair.
 *
 * Both branches are stated separately because they call for different next
 * steps: a total failure is "try again", a partial one is "the rest is done,
 * retry only what is left".
 */
export function reviewOutcomeNotice(
  saved: number,
  failed: number,
  /**
   * How many of the failures were refused because the TOOL CHANGED while the
   * dialog was open.
   *
   * Said separately because it is the one refusal with a different next step.
   * "Try again" is wrong advice here — resubmitting the same decision against
   * the same shape they were shown will be refused again, every time. What they
   * have to do first is look at the version the server now offers, which is a
   * different act from retrying.
   */
  stale = 0,
): ReviewOutcomeNotice {
  const refused = `${toolCount(failed)} could not be saved and kept the previous policy.`;
  const next =
    stale > 0
      ? `${toolCount(stale)} changed while you were reviewing — the list has been refreshed, so review the latest version before deciding again.`
      : null;
  if (saved === 0) {
    return {
      title:
        stale > 0
          ? "These tools changed while you were reviewing them"
          : "This review was not saved",
      body: next ?? `${refused} Nothing changed — try again.`,
    };
  }
  return {
    title: "Only part of this review was saved",
    body:
      `${toolCount(saved)} ${saved === 1 ? "was" : "were"} applied and ` +
      `${saved === 1 ? "is" : "are"} in effect now. ${refused} ` +
      (next ?? "Try the rest again."),
  };
}

/**
 * Was this refusal "the tool changed under you"?
 *
 * Duck-typed on the envelope rather than on `instanceof`, deliberately: this is
 * classifying a REJECTION REASON out of `Promise.allSettled`, whose type is
 * `unknown`, and a narrowing that depended on the error class would silently
 * stop matching if the client ever wrapped it.
 */
export function isStaleReviewRejection(reason: unknown): boolean {
  const envelope = (reason as { envelope?: { code?: unknown } } | null)
    ?.envelope;
  return envelope?.code === "INTEGRATION_CATALOG_REVIEW_STALE";
}

/** Free-text match over what the reviewer can see. Lifecycle is a tab, not a word. */
export function matchesSearch(
  entry: IntegrationToolCatalogEntry,
  needle: string,
): boolean {
  if (needle === "") return true;
  return `${entry.title ?? ""} ${entry.toolName} ${entry.description ?? ""}`
    .toLocaleLowerCase()
    .includes(needle);
}

/** Narrow by the ADMINISTRATOR-CONFIRMED class — never by the server's hint. */
export function matchesRisk(
  entry: IntegrationToolCatalogEntry,
  filter: RiskFilter,
): boolean {
  if (filter === "all") return true;
  if (filter === "unclassified") return entry.riskClass === null;
  return entry.riskClass === filter;
}

export function lifecycleCounts(
  entries: readonly IntegrationToolCatalogEntry[],
): Readonly<Record<RemoteToolLifecycle, number>> {
  const counts: Record<RemoteToolLifecycle, number> = {
    quarantined: 0,
    enabled: 0,
    disabled: 0,
    withdrawn: 0,
  };
  for (const entry of entries) counts[entry.lifecycle] += 1;
  return counts;
}

/**
 * The most recent moment the server was observed still offering any of these
 * tools — the only sync timestamp this contract carries.
 *
 * Null for an empty catalogue, deliberately: there is no sighting to report, and
 * a fabricated "last synced" is a claim about a request that may never have been
 * made. Compared as instants rather than as strings, because the contract types
 * these as `date-time` and an offset form would sort wrong lexically.
 */
export function lastSeenAt(
  entries: readonly IntegrationToolCatalogEntry[],
): string | null {
  let best: { iso: string; ms: number } | null = null;
  for (const entry of entries) {
    const ms = Date.parse(entry.lastSeenAt);
    if (Number.isNaN(ms)) continue;
    if (best === null || ms > best.ms) best = { iso: entry.lastSeenAt, ms };
  }
  return best === null ? null : best.iso;
}
